import React, { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import { AuthProvider } from './context/AuthContext';
import { WorkspaceProvider } from './context/WorkspaceContext';

// Pages
import Home from './pages/Home';
import MarketingHome from './pages/MarketingHome';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import CreateSession from './pages/CreateSession';
import JoinSession from './pages/JoinSession';
import Session from './pages/Session';
import NotFound from './pages/NotFound';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import DashboardPage from './pages/DashboardPage';
import UserSettingsPage from './pages/UserSettingsPage';
import AccountPage from './pages/AccountPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import SessionReflectionsPage from './pages/SessionReflectionsPage';
import SessionRetroPage from './pages/SessionRetroPage';
import SSOCallback from './pages/SSOCallback';
import GoogleCallback from './pages/GoogleCallback';
import AnalyticsDashboardPage from './pages/AnalyticsDashboardPage';
import ShapeUpPage from './pages/ShapeUpPage';
import CyclesPage from './pages/CyclesPage';
import ShapeUpAnalyticsPage from './pages/ShapeUpAnalyticsPage';
import TagPage from './pages/TagPage';
import BugsPage from './pages/BugsPage';
import BetDetailPage from './pages/BetDetailPage';
import PitchesPage from './pages/PitchesPage';
import ScratchpadPage from './pages/ScratchpadPage';
import OrganizationsPage from './pages/OrganizationsPage';
import OrganizationDetailPage from './pages/OrganizationDetailPage';
import OrganizationSettingsPage from './pages/OrganizationSettingsPage';
import TeamDetailPage from './pages/TeamDetailPage';
import InvitePage from './pages/InvitePage';
import OnboardingPage from './pages/OnboardingPage';
import StakeholderViewsListPage from './pages/StakeholderViewsListPage';
import StakeholderViewPage from './pages/StakeholderViewPage';
import PublicStatusPage from './pages/PublicStatusPage';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import ProtectedRoute from './components/routing/ProtectedRoute';
import ScrumRoute from './components/routing/ScrumRoute';
import ConnectionStatus from './components/ConnectionStatus';
import FeedbackButton from './components/feedback/FeedbackButton';

// Create a dark theme instance
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#3b82f6',      // Accent blue
      dark: '#2563EB',      // Darker blue for hover states
      light: '#60A5FA',     // Lighter blue for accents
      contrastText: '#FFFFFF'
    },
    secondary: {
      main: '#8b5cf6',      // Purple accent for gradients
      dark: '#7c3aed',
      light: '#a78bfa',
      contrastText: '#FFFFFF'
    },
    error: {
      main: '#ef4444',      // Error/outlier red
      dark: '#DC2626',
      light: '#F87171'
    },
    warning: {
      main: '#F59E0B',      // Amber warning
      dark: '#D97706',
      light: '#FCD34D'
    },
    success: {
      main: '#22c55e',      // Success/consensus green
      dark: '#16a34a',
      light: '#4ade80'
    },
    info: {
      main: '#06B6D4',      // Cyan info
      dark: '#0891B2',
      light: '#22D3EE'
    },
    background: {
      default: '#0a0a0a',   // Dark page background
      paper: '#1a1a1a',     // Dark surface for cards/papers
    },
    text: {
      primary: '#e5e5e5',   // Light text for headings
      secondary: '#a3a3a3', // Medium gray for body text
      disabled: '#737373'   // Muted text
    },
    divider: '#2a2a2a',     // Dark border color
    grey: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717'
    }
  },
  typography: {
    fontFamily: 'var(--font-family-sans-serif)',
    h1: {
      fontWeight: 'var(--font-weight-bold)',
      fontSize: 'calc(var(--font-size-base) * 2.5)',
    },
    h2: {
      fontWeight: 'var(--font-weight-bold)',
      fontSize: 'calc(var(--font-size-base) * 2)',
    },
    h3: {
      fontWeight: 'var(--font-weight-bold)',
      fontSize: 'calc(var(--font-size-base) * 1.75)',
    },
    h4: {
      fontWeight: 'var(--font-weight-medium)',
      fontSize: 'calc(var(--font-size-base) * 1.5)',
    },
    h5: {
      fontWeight: 'var(--font-weight-medium)',
      fontSize: 'calc(var(--font-size-base) * 1.25)',
    },
    h6: {
      fontWeight: 'var(--font-weight-medium)',
      fontSize: 'var(--font-size-base)',
    },
    body1: {
      fontSize: 'var(--font-size-base)',
      fontWeight: 'var(--font-weight-normal)',
    },
    body2: {
      fontSize: 'calc(var(--font-size-base) * 0.875)',
      fontWeight: 'var(--font-weight-light)',
    },
    button: {
      textTransform: 'none',
      fontWeight: 'var(--font-weight-medium)',
    },
  },
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a0a0a',    // Match dark background
          color: '#e5e5e5',              // Light text
          boxShadow: 'none',
          borderBottom: '1px solid #2a2a2a',  // Dark border
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '8px 16px',
        },
        containedPrimary: {
          backgroundColor: '#3b82f6',
          color: '#FFFFFF',
          boxShadow: '0 1px 2px 0 rgba(59, 130, 246, 0.3)',
          '&:hover': {
            backgroundColor: '#2563EB',
            boxShadow: '0 2px 4px 0 rgba(59, 130, 246, 0.4)',
          },
        },
        outlinedPrimary: {
          borderColor: '#3b82f6',
          color: '#3b82f6',
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderColor: '#2563EB',
          }
        },
        text: {
          color: '#a3a3a3',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          }
        }
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'var(--surface-color)',
          border: 'var(--border-width) solid var(--border-color)',
          color: 'var(--text-color)',
        },
        elevation1: {
          boxShadow: 'var(--box-shadow-sm)',
        },
        elevation2: {
          boxShadow: 'var(--box-shadow)',
        },
        elevation3: {
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1a1a1a',
          borderRadius: 12,
          border: '1px solid #2a2a2a',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.3)',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.4)',
            borderColor: '#404040',
          }
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
        colorPrimary: {
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          color: '#60A5FA',
          '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.25)',
          }
        },
        colorSuccess: {
          backgroundColor: 'rgba(34, 197, 94, 0.15)',
          color: '#4ade80',
        },
        colorWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          color: '#fbbf24',
        },
        colorError: {
          backgroundColor: 'rgba(239, 68, 68, 0.15)',
          color: '#f87171',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: 'var(--primary-color)',
          '&:hover': {
            color: 'var(--primary-color-dark)',
          },
        },
      },
    },
  },
});

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Handle redirects for the old /session/:id route to /meeting/:id
  useEffect(() => {
    const parts = location.pathname.split('/');
    // parts will be like: ["", "session", "actualSessionId"] for /session/actualSessionId
    // parts will be like: ["", "session", "actualSessionId", "retrospective"] for /session/actualSessionId/retrospective

    // Only redirect if the path is exactly /session/:sessionId (length 3 after split)
    if (parts[0] === '' && parts[1] === 'session' && parts[2] && parts.length === 3) {
      const sessionId = parts[2];
      // Further check to ensure sessionId is not a sub-path keyword, though length check is primary
      if (sessionId !== 'retrospective') { // Add other keywords if necessary
        console.log(`Redirecting old /session/${sessionId} → /meeting/${sessionId}`);
        navigate(`/meeting/${sessionId}`, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // Set dark background for all pages
  useEffect(() => {
    document.body.style.backgroundColor = '#0a0a0a';
  }, []);
  
  return (
    <AuthProvider>
      <WorkspaceProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Navbar />
        <Container
          component="main"
          maxWidth={false}
          sx={{
            pt: '70px', // Account for fixed navbar height
            pb: 0,
            px: 0,
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
            backgroundColor: '#0a0a0a'
          }}
        >
          {/* Hide ConnectionStatus on marketing pages and auth pages */}
          {!['/'].includes(location.pathname) &&
           !location.pathname.startsWith('/features') &&
           !location.pathname.startsWith('/pricing') &&
           !location.pathname.startsWith('/login') &&
           !location.pathname.startsWith('/register') && (
            <ConnectionStatus />
          )}
          <FeedbackButton />
          <Routes>
            <Route path="/" element={<MarketingHome />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/app" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/sso/callback" element={<SSOCallback />} />
            <Route path="/auth/google/callback" element={<GoogleCallback />} />
            
            {/* First-time onboarding (authenticated, but exempt from the onboarding gate) */}
            <Route path="/onboarding" element={<ProtectedRoute sessionRequired={false} allowWithoutOnboarding><OnboardingPage /></ProtectedRoute>} />

            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute sessionRequired={false}><ScrumRoute><DashboardPage /></ScrumRoute></ProtectedRoute>} />
            <Route path="/create-session" element={<ProtectedRoute sessionRequired={false}><ScrumRoute><CreateSession /></ScrumRoute></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute sessionRequired={false}><UserSettingsPage /></ProtectedRoute>} />
            <Route path="/account" element={<ProtectedRoute sessionRequired={false}><AccountPage /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute sessionRequired={false}><AnalyticsDashboardPage /></ProtectedRoute>} />
            <Route path="/shape-up" element={<ProtectedRoute sessionRequired={false}><ShapeUpPage /></ProtectedRoute>} />
            <Route path="/cycles" element={<ProtectedRoute sessionRequired={false}><CyclesPage /></ProtectedRoute>} />
            <Route path="/shape-up/analytics" element={<ProtectedRoute sessionRequired={false}><ShapeUpAnalyticsPage /></ProtectedRoute>} />
            <Route path="/shape-up/bets/:betId" element={<ProtectedRoute sessionRequired={false}><BetDetailPage /></ProtectedRoute>} />
            <Route path="/shape-up/tags/:slug" element={<ProtectedRoute sessionRequired={false}><TagPage /></ProtectedRoute>} />
            <Route path="/shape-up/pitches" element={<ProtectedRoute sessionRequired={false}><PitchesPage /></ProtectedRoute>} />
            <Route path="/shape-up/stakeholders" element={<ProtectedRoute sessionRequired={false}><StakeholderViewsListPage /></ProtectedRoute>} />
            <Route path="/shape-up/stakeholders/:viewId" element={<ProtectedRoute sessionRequired={false}><StakeholderViewPage /></ProtectedRoute>} />
            <Route path="/bugs" element={<ProtectedRoute sessionRequired={false}><BugsPage /></ProtectedRoute>} />
            <Route path="/scratchpad" element={<ProtectedRoute sessionRequired={false}><ScratchpadPage /></ProtectedRoute>} />

            {/* Organization & Team Routes */}
            <Route path="/organizations" element={<ProtectedRoute sessionRequired={false}><OrganizationsPage /></ProtectedRoute>} />
            <Route path="/organizations/:orgId" element={<ProtectedRoute sessionRequired={false}><OrganizationDetailPage /></ProtectedRoute>} />
            <Route path="/organizations/:orgId/settings" element={<ProtectedRoute sessionRequired={false}><OrganizationSettingsPage /></ProtectedRoute>} />
            <Route path="/teams/:teamId" element={<ProtectedRoute sessionRequired={false}><TeamDetailPage /></ProtectedRoute>} />

            {/* Invite Routes (partially public) */}
            <Route path="/invite/:type/:token" element={<InvitePage />} />
            <Route 
              path="/admin" 
              element={(
                <ProtectedRoute sessionRequired={false} adminRequired={true}>
                  <AdminDashboardPage />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/session/:sessionId/reflections"
              element={(
                <ProtectedRoute sessionRequired={false}>
                  <SessionReflectionsPage />
                </ProtectedRoute>
              )}
            />
            <Route
              path="/session/:sessionId/retro"
              element={(
                <ProtectedRoute sessionRequired={false}>
                  <SessionRetroPage />
                </ProtectedRoute>
              )}
            />
            
            {/* 
              The /join/:sessionId route does not require authentication initially.
              Authentication might be required by the JoinSessionPage itself or by subsequent actions.
            */}
            <Route path="/join/:sessionId" element={<JoinSession />} />

            {/* Public stakeholder status page — no login, resolved from an opaque token. */}
            <Route path="/status/:token" element={<PublicStatusPage />} />
            
            {/* 
              The /meeting/:sessionId route should be a ProtectedRoute if direct access is to be restricted 
              to authenticated users only. However, existing logic tries to recover sessions and might handle this.
              For now, let's assume Session page handles its own auth checks or public access parts.
              If it needs to be strictly private, wrap with <ProtectedRoute>.
            */}
            <Route path="/meeting/:sessionId" element={<Session />} />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Container>
          <Footer />
        </ThemeProvider>
      </WorkspaceProvider>
    </AuthProvider>
  );
}

export default App; 